// apps/web/src/features/clients/components/ClientCreateModal.tsx
//
// Create-client modal — composed of two sections (`Client` and `First
// contact`) wired to one Zod schema. The backend atomically creates the
// client + first primary contact inside one transaction; the form's
// success/error path matches that.
//
// Unsaved-changes guard: all close paths (Modal onClose, Cancel, Escape,
// backdrop) are routed through the EditorModal wrapper guard. Dirty state is
// tracked via a local boolean rather than RHF formState.isDirty because
// the PhoneInput library fires onChange with the dial-code on mount ("+7"
// for RU), which would make isDirty=true before the user touches anything.
// The watch() subscription is registered after child effects complete, so
// it never catches that initial library-initiated onChange.
// Successful create resets dirty so no stale warning appears on redirect.
//
// INVARIANT (AC-9): the create POST stays ATOMIC — client + first primary
// contact in one request. When the request gets no verdict (network drop), the
// modal does NOT resubmit: the client may already exist, and a blind replay
// would create a duplicate. It reports the uncertainty and offers a refresh.
//
// Identifier conflict (CRM v4 M1/M6, #2242/#2247): a colliding first-contact
// phone hard-blocks with the registry-native 409 `DOMAIN_CONFLICT` +
// `meta.kind: 'IDENTIFIER_TAKEN'`. When the taken identifier resolves to a live
// client, `meta.owner = { clientId, managerId }` (ids only, NEVER a phone or
// name) lets the modal offer an "open the owning client" action inline; when it
// does not resolve to a client, a generic "already in use" message shows with no
// navigation. The typed input is kept intact either way.
import { useCallback, useEffect, useId, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  CLIENT_CONTACT_FULL_NAME_MAX_LENGTH,
  CLIENT_NAME_MAX_LENGTH,
  CreateClientInputSchema,
  ErrorCode,
  type CreateClientInput,
  type ClientDetail,
} from '@magimp/shared';
import { clientsKeys, useCreateClient } from '@magimp/shared-hooks';
import {
  Banner,
  Button,
  EditorModal,
  FormRow,
  Input,
  PhoneInput,
  Textarea,
  useEditorGuard,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { ApiClientError } from '../../../api/errors.js';
import {
  phoneError,
  parseIdentifierTakenMeta,
  type IdentifierTakenInfo,
} from '../../../shared/index.js';
import { useClientFieldError } from '../useClientFieldError.js';
import { useClientSubmitError } from '../useClientSubmitError.js';
import { useMobileFieldStyle, useTouchActionStyle, useTouchInput } from '../useMobileFieldStyle.js';
import { ClientIdentifierTakenBlock } from './ClientIdentifierTakenBlock.js';
import { EditorCancelButton } from './EditorCancelButton.js';

export interface ClientCreateModalProps {
  onClose: () => void;
}

const DEFAULTS: CreateClientInput = {
  name: '',
  comment: null,
  firstContact: { fullName: '', phone: '', comment: null },
};

export function ClientCreateModal({ onClose }: ClientCreateModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const create = useCreateClient();
  // Owned here so the sheet FOOTER can reflect them: on a narrow screen the
  // actions belong at the bottom of the sheet, not below a long form body.
  const submitError = useClientSubmitError();
  const actionStyle = useTouchActionStyle();
  const formId = useId();

  // WHY: PhoneInput (react-international-phone) fires onChange with the dial
  // code on mount ("+7" for RU) which would make formState.isDirty=true
  // before user interaction. We use a local dirty state initialized false and
  // set it via the watch() subscription — which is registered after child
  // effects run, so it misses the library's init call. See file header.
  const [dirty, setDirty] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<CreateClientInput>({
    resolver: zodResolver(CreateClientInputSchema),
    defaultValues: DEFAULTS,
  });

  // Subscribe via watch() to track user-initiated changes. The subscription
  // is registered in a parent-component effect — after PhoneInput's child
  // effects have already fired — so the dial-code init call is never caught.
  useEffect(() => {
    const subscription = watch(() => setDirty(true));
    return () => subscription.unsubscribe();
  }, [watch]);

  // Reset dirty on unmount so the provider has no stale signal.
  useEffect(() => () => setDirty(false), []);

  const resetDirty = useCallback(() => setDirty(false), []);

  return (
    <EditorModal
      open={true}
      title={t('clients:modals.createTitle')}
      width={520}
      narrowFullWidth
      dirty={dirty}
      busy={create.isPending}
      onConfirmClose={onClose}
      footer={
        <>
          <EditorCancelButton
            onClose={onClose}
            label={t('common:actions.cancel')}
            style={actionStyle}
          />
          <Button
            type="submit"
            form={formId}
            kind="primary"
            style={actionStyle}
            disabled={create.isPending || submitError.state.uncertain}
            data-testid="client-create-submit"
          >
            {t('common:actions.create')}
          </Button>
        </>
      }
    >
      <ClientCreateForm
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        setError={setError}
        create={create}
        submitError={submitError}
        formId={formId}
        resetDirty={resetDirty}
        onClose={onClose}
      />
    </EditorModal>
  );
}

// WHY: form body is split out so `useEditorGuard()` can be called from inside
// the EditorModal subtree. `bypassNext` is required because the synchronous
// `navigate(...)` in `onSuccess` would otherwise be intercepted by the route
// blocker reading the still-uncommitted dirty=true. `confirmDestructive` is
// used for the Cancel button (in-form close affordance) so a dirty form still
// prompts before discarding typed input.
function ClientCreateForm({
  control,
  errors,
  handleSubmit,
  setError,
  create,
  submitError,
  formId,
  resetDirty,
  onClose,
}: {
  control: ReturnType<typeof useForm<CreateClientInput>>['control'];
  errors: ReturnType<typeof useForm<CreateClientInput>>['formState']['errors'];
  handleSubmit: ReturnType<typeof useForm<CreateClientInput>>['handleSubmit'];
  setError: ReturnType<typeof useForm<CreateClientInput>>['setError'];
  create: ReturnType<typeof useCreateClient>;
  submitError: ReturnType<typeof useClientSubmitError>;
  formId: string;
  resetDirty: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const show = useToastStore((s) => s.show);
  const queryClient = useQueryClient();
  const { bypassNext, confirmDestructive } = useEditorGuard();
  const [identifierTaken, setIdentifierTaken] = useState<IdentifierTakenInfo | null>(null);
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const touchInput = useTouchInput();

  const onSubmit = (values: CreateClientInput) => {
    // An unresolved create is never replayed, from any submit path: the client
    // may already exist and a second POST would duplicate it (AC-9).
    if (submitError.state.uncertain) return;
    setIdentifierTaken(null);
    submitError.clear();
    create.mutate(values, {
      onSuccess: (created: ClientDetail) => {
        // Reset dirty before onClose fires so the guard sees a clean form.
        resetDirty();
        // `setDirty(false)` schedules a re-render but the synchronous
        // `navigate(...)` below would otherwise be intercepted by the
        // blocker reading the still-uncommitted dirty=true. `bypassNext()`
        // is the documented one-shot escape for this case.
        bypassNext();
        show({ tone: 'success', text: t('clients:toasts.created') });
        onClose();
        navigate(`/clients/${created.id}`);
      },
      onError: (err) => {
        // CRM v4 (#2242/#2247): a colliding identifier hard-blocks with the
        //   registry-native `DOMAIN_CONFLICT` + `meta.kind: 'IDENTIFIER_TAKEN'`.
        //   Surface it inline (with an open-owning-client action when the owner
        //   resolves to a live client) and keep the typed input intact. Any
        //   other error stays a toast.
        if (err instanceof ApiClientError && err.code === ErrorCode.DOMAIN_CONFLICT) {
          const taken = parseIdentifierTakenMeta(err.meta);
          if (taken) {
            setIdentifierTaken(taken);
            return;
          }
        }
        // Everything else stays IN the form: a duplicate slot is reported on
        // its field, an unknown outcome as a warning that blocks resubmission.
        submitError.report(err, (field, message) => {
          if (field === 'name') setError('name', { message }, { shouldFocus: true });
          if (field === 'phone') setError('firstContact.phone', { message }, { shouldFocus: true });
        });
      },
    });
  };

  // Unknown outcome: only discard the unresolved draft after an explicit
  // confirmation, then refresh the directory instead of replaying Create.
  const checkResult = () => {
    confirmDestructive(() => {
      void queryClient.invalidateQueries({ queryKey: clientsKeys.all });
      onClose();
    });
  };

  const onOpenOwner = () => {
    const owner = identifierTaken?.owner;
    if (!owner) return;
    // WHY (#1363 F-R2-001): `bypassNext()` lets the synchronous navigate slip
    // past the unsaved-changes blocker (the form is still dirty). The blocker's
    // same-location early-return does NOT consume the one-shot bypass on a
    // no-op nav, so arm it ONLY when the target actually changes the URL — else
    // the armed flag leaks to the NEXT real navigation and defeats the guard.
    const target = `/clients/${owner.clientId}`;
    if (location.pathname !== target) bypassNext();
    navigate(`/clients/${encodeURIComponent(owner.clientId)}`);
    onClose();
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitError.state.text !== null && (
        <div style={{ marginBottom: 'var(--s-3)' }}>
          <Banner tone={submitError.state.uncertain ? 'warn' : 'error'}>
            {submitError.state.uncertain ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--s-2)',
                  minWidth: 0,
                }}
              >
                <div>{submitError.state.text}</div>
                <Button
                  size="sm"
                  style={{ ...actionStyle, alignSelf: 'flex-start', maxWidth: '100%' }}
                  onClick={checkResult}
                  data-testid="client-create-check"
                >
                  {t('clients:actions.checkClientCreateResult')}
                </Button>
              </div>
            ) : (
              submitError.state.text
            )}
          </Banner>
        </div>
      )}
      <FormRow
        label={t('clients:fields.name')}
        error={fieldError(errors.name, { kind: 'text', max: CLIENT_NAME_MAX_LENGTH })}
      >
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              ref={field.ref}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.name}
              maxLength={CLIENT_NAME_MAX_LENGTH}
              style={fieldStyle}
            />
          )}
        />
      </FormRow>
      <FormRow
        label={t('clients:fields.comment')}
        error={fieldError(errors.comment, { kind: 'text' })}
      >
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Textarea
              ref={field.ref}
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v === '' ? null : v)}
              onBlur={field.onBlur}
              invalid={!!errors.comment}
              style={fieldStyle}
            />
          )}
        />
      </FormRow>
      <h3
        style={{
          fontSize: 'var(--fz-13)',
          fontWeight: 600,
          margin: 'var(--s-4) 0 var(--s-2)',
        }}
      >
        {t('clients:sections.firstContact')}
      </h3>
      <FormRow
        label={t('clients:fields.contactFullName')}
        error={fieldError(errors.firstContact?.fullName, {
          kind: 'text',
          max: CLIENT_CONTACT_FULL_NAME_MAX_LENGTH,
        })}
      >
        <Controller
          control={control}
          name="firstContact.fullName"
          render={({ field }) => (
            <Input
              ref={field.ref}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.firstContact?.fullName}
              maxLength={CLIENT_CONTACT_FULL_NAME_MAX_LENGTH}
              style={fieldStyle}
            />
          )}
        />
      </FormRow>
      <FormRow
        label={t('clients:fields.phone')}
        hint={t('clients:hints.phoneFormat')}
        error={phoneError(errors.firstContact?.phone?.message, t('clients:errors.invalidPhone'))}
      >
        <Controller
          control={control}
          name="firstContact.phone"
          render={({ field }) => (
            <PhoneInput
              ref={field.ref}
              value={field.value ?? ''}
              onChange={field.onChange}
              invalid={!!errors.firstContact?.phone}
              testId="client-create-phone"
              style={fieldStyle}
              // The country trigger and its portalled picker are tapped on the
              // same phone as this form's other controls.
              touchTargets={touchInput}
            />
          )}
        />
      </FormRow>
      <FormRow
        label={t('clients:fields.contactComment')}
        error={fieldError(errors.firstContact?.comment, { kind: 'text' })}
      >
        <Controller
          control={control}
          name="firstContact.comment"
          render={({ field }) => (
            <Textarea
              ref={field.ref}
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v === '' ? null : v)}
              onBlur={field.onBlur}
              invalid={!!errors.firstContact?.comment}
              style={fieldStyle}
            />
          )}
        />
      </FormRow>
      {identifierTaken && (
        <ClientIdentifierTakenBlock
          info={identifierTaken}
          onOpenOwner={onOpenOwner}
          actionStyle={actionStyle}
        />
      )}
    </form>
  );
}
